// Pengaturan CSAT — editor pertanyaan survei + template pesan WA undangan.
// Halaman BARU (Fase 5.2): sebelumnya template_pesan (routers/csat.py) sudah
// bisa disimpan lewat PUT /csat/pengaturan tapi TIDAK PUNYA UI sama sekali.
// Izin dipakai SAMA dengan modul "csat" yang sudah ada (deps.py) — "view" untuk
// buka halaman, "update" untuk mengedit/menyimpan (lihat backend require_permission).
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Star, MessageSquareText, ListChecks, Save, Plus, Trash2, ArrowUp, ArrowDown,
  Settings2, MessageCircle, ArrowLeft,
} from "lucide-react";

const TIPE_OPTIONS = [
  { value: "bintang", label: "Bintang (1-5)", icon: Star },
  { value: "teks", label: "Teks bebas", icon: MessageSquareText },
  { value: "pilihan", label: "Pilihan ganda", icon: ListChecks },
];

// ID unik untuk pertanyaan baru — cukup untuk kebutuhan editor ini (bukan
// dipakai sebagai secret/token apa pun), tidak perlu crypto.randomUUID yang
// dukungan browsernya lebih sempit.
const idBaru = () => `q_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

const baris_kosong = () => ({
  id: idBaru(), tipe: "bintang", label: "", wajib: true, urutan: 0, opsi: [], rating_utama: false,
});

export default function CsatPengaturan() {
  const { can } = useAuth();
  const canEdit = can("csat", "update");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templatePesan, setTemplatePesan] = useState("");
  const [templateDefault, setTemplateDefault] = useState("");
  const [pertanyaan, setPertanyaan] = useState([]);

  const load = () => {
    setLoading(true);
    api.get("/csat/pengaturan")
      .then((r) => {
        const d = r.data || {};
        setTemplatePesan(d.template_pesan || "");
        setTemplateDefault(d.template_default || "");
        setPertanyaan((d.pertanyaan || []).map((p) => ({ ...p, opsi: p.opsi || [] })));
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Gagal memuat pengaturan CSAT"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  // ---------- Validasi ringan di klien (server tetap wasit terakhir) ----------
  const masalah = useMemo(() => {
    const daftar = [];
    if (pertanyaan.length === 0) daftar.push("Minimal harus ada 1 pertanyaan survei.");
    const idKosong = pertanyaan.some((p) => !p.label.trim());
    if (idKosong) daftar.push("Semua pertanyaan wajib punya label/judul.");
    const adaBintang = pertanyaan.some((p) => p.tipe === "bintang");
    if (!adaBintang) daftar.push("Minimal harus ada 1 pertanyaan bertipe Bintang — dipakai untuk KPI CSAT karyawan.");
    const pilihanKurangOpsi = pertanyaan.some((p) => p.tipe === "pilihan" && (p.opsi || []).filter((o) => o.trim()).length < 2);
    if (pilihanKurangOpsi) daftar.push("Pertanyaan bertipe Pilihan wajib punya minimal 2 opsi.");
    return daftar;
  }, [pertanyaan]);

  const utamaSaatIni = pertanyaan.find((p) => p.tipe === "bintang" && p.rating_utama);

  // ---------- Aksi baris ----------
  const ubahBaris = (idx, patch) => setPertanyaan((s) => s.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  const hapusBaris = (idx) => setPertanyaan((s) => s.filter((_, i) => i !== idx));
  const tambahBaris = () => setPertanyaan((s) => [...s, baris_kosong()]);
  const pindahBaris = (idx, arah) => setPertanyaan((s) => {
    const tujuan = idx + arah;
    if (tujuan < 0 || tujuan >= s.length) return s;
    const salinan = [...s];
    [salinan[idx], salinan[tujuan]] = [salinan[tujuan], salinan[idx]];
    return salinan;
  });
  const jadikanRatingUtama = (idx) => setPertanyaan((s) => s.map((p, i) => ({ ...p, rating_utama: i === idx })));
  const ubahTipe = (idx, tipeBaru) => setPertanyaan((s) => s.map((p, i) => {
    if (i !== idx) return p;
    return {
      ...p,
      tipe: tipeBaru,
      opsi: tipeBaru === "pilihan" ? (p.opsi && p.opsi.length ? p.opsi : ["", ""]) : [],
      rating_utama: tipeBaru === "bintang" ? p.rating_utama : false,
    };
  }));
  const ubahOpsi = (idx, oIdx, nilai) => setPertanyaan((s) => s.map((p, i) => {
    if (i !== idx) return p;
    const opsi = [...(p.opsi || [])];
    opsi[oIdx] = nilai;
    return { ...p, opsi };
  }));
  const tambahOpsi = (idx) => setPertanyaan((s) => s.map((p, i) => (i === idx ? { ...p, opsi: [...(p.opsi || []), ""] } : p)));
  const hapusOpsi = (idx, oIdx) => setPertanyaan((s) => s.map((p, i) => (i === idx ? { ...p, opsi: (p.opsi || []).filter((_, j) => j !== oIdx) } : p)));

  // ---------- Simpan ----------
  const save = async () => {
    if (masalah.length > 0) {
      toast.error(masalah[0]);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        template_pesan: templatePesan.trim(),
        pertanyaan: pertanyaan.map((p, i) => ({
          id: p.id.trim(),
          tipe: p.tipe,
          label: p.label.trim(),
          wajib: !!p.wajib,
          urutan: i + 1,
          opsi: p.tipe === "pilihan" ? (p.opsi || []).map((o) => o.trim()).filter(Boolean) : [],
          rating_utama: p.tipe === "bintang" ? !!p.rating_utama : false,
        })),
      };
      const { data } = await api.put("/csat/pengaturan", payload);
      setTemplatePesan(data.template_pesan || "");
      if (data.pertanyaan) setPertanyaan(data.pertanyaan.map((p) => ({ ...p, opsi: p.opsi || [] })));
      toast.success("Pengaturan CSAT disimpan");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan pengaturan CSAT");
    }
    setSaving(false);
  };

  if (loading) return <div className="p-8 text-sm text-slate-500">Memuat...</div>;

  return (
    <div className="p-4 md:p-8 max-w-[1100px]" data-testid="csat-pengaturan-page">
      <header className="flex items-start justify-between mb-6 md:mb-8 flex-wrap gap-4">
        <div>
          <Link to="/csat-hasil" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-emerald-600 mb-2" data-testid="csat-pengaturan-kembali">
            <ArrowLeft className="w-3.5 h-3.5" /> Kembali ke Hasil CSAT
          </Link>
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-2 flex items-center gap-1.5">
            <Settings2 className="w-3.5 h-3.5" /> Pengaturan
          </p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Pengaturan Survei CSAT</h1>
          <p className="mt-2 text-sm text-slate-500 max-w-2xl">
            Atur pertanyaan yang ditanyakan ke pelanggan di halaman survei publik, tentukan mana yang WAJIB diisi,
            dan ubah teks undangan WhatsApp. Instalasi lama otomatis memakai 1 pertanyaan bintang (wajib) + 1 komentar (opsional) sampai diedit di sini.
          </p>
        </div>
        {canEdit && (
          <button onClick={save} disabled={saving} data-testid="csat-pengaturan-simpan"
            className="h-10 px-5 rounded-lg bg-[#059669] hover:bg-[#047857] text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60">
            <Save className="w-4 h-4" /> {saving ? "Menyimpan..." : "Simpan Pengaturan"}
          </button>
        )}
      </header>

      {!canEdit && (
        <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Anda hanya bisa melihat pengaturan ini. Hubungi admin/manager untuk melakukan perubahan.
        </div>
      )}

      {canEdit && masalah.length > 0 && (
        <div className="mb-6 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700" data-testid="csat-pengaturan-masalah">
          <p className="font-semibold mb-1">Belum bisa disimpan:</p>
          <ul className="list-disc list-inside space-y-0.5">
            {masalah.map((m, i) => <li key={i}>{m}</li>)}
          </ul>
        </div>
      )}

      {/* ---------- Daftar Pertanyaan ---------- */}
      <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 mb-6">
        <div className="flex items-center gap-2 mb-1">
          <ListChecks className="w-4 h-4 text-emerald-600" />
          <h2 className="font-display text-base font-bold text-slate-900">Pertanyaan Survei</h2>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          Pelanggan mengisi pertanyaan ini secara berurutan di halaman publik. Centang <b>Wajib</b> untuk mengunci
          tombol "Kirim Penilaian" sampai pertanyaan itu terisi — validasinya ditegakkan di server, bukan cuma di layar pelanggan.
          {utamaSaatIni ? (
            <> Pertanyaan bintang yang jadi <b>nilai KPI CSAT karyawan</b> saat ini: <b>{utamaSaatIni.label || "(tanpa label)"}</b>.</>
          ) : (
            <> Belum ada yang ditandai sebagai rating utama — saat disimpan, pertanyaan bintang PERTAMA otomatis dipakai untuk KPI CSAT karyawan.</>
          )}
        </p>

        <div className="space-y-4">
          {pertanyaan.map((p, idx) => {
            const TipeIcon = (TIPE_OPTIONS.find((t) => t.value === p.tipe) || TIPE_OPTIONS[0]).icon;
            return (
              <div key={p.id} className="border border-slate-200 rounded-lg p-4" data-testid={`csat-pertanyaan-row-${idx}`}>
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 grid place-items-center shrink-0">
                    <TipeIcon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-[220px] space-y-2">
                    <input
                      type="text" value={p.label} disabled={!canEdit}
                      onChange={(e) => ubahBaris(idx, { label: e.target.value })}
                      placeholder="Judul pertanyaan, mis. Bagaimana pelayanan kami hari ini?"
                      data-testid={`csat-pertanyaan-label-${idx}`}
                      className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm disabled:bg-slate-50"
                    />
                    <div className="flex flex-wrap items-center gap-3">
                      <select value={p.tipe} disabled={!canEdit} onChange={(e) => ubahTipe(idx, e.target.value)}
                        data-testid={`csat-pertanyaan-tipe-${idx}`}
                        className="h-8 px-2 rounded-lg border border-slate-200 text-xs bg-white disabled:bg-slate-50">
                        {TIPE_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                      <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none">
                        <input type="checkbox" checked={!!p.wajib} disabled={!canEdit}
                          onChange={(e) => ubahBaris(idx, { wajib: e.target.checked })}
                          data-testid={`csat-pertanyaan-wajib-${idx}`}
                          className="w-3.5 h-3.5 accent-emerald-600" />
                        Wajib diisi
                      </label>
                      {p.tipe === "bintang" && (
                        <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none">
                          <input type="radio" name="rating_utama" checked={!!p.rating_utama} disabled={!canEdit}
                            onChange={() => jadikanRatingUtama(idx)}
                            data-testid={`csat-pertanyaan-utama-${idx}`}
                            className="w-3.5 h-3.5 accent-emerald-600" />
                          Rating utama (nilai KPI karyawan)
                        </label>
                      )}
                    </div>
                    {p.tipe === "pilihan" && (
                      <div className="pl-1 space-y-1.5" data-testid={`csat-pertanyaan-opsi-wrap-${idx}`}>
                        {(p.opsi || []).map((o, oIdx) => (
                          <div key={oIdx} className="flex items-center gap-2">
                            <input type="text" value={o} disabled={!canEdit}
                              onChange={(e) => ubahOpsi(idx, oIdx, e.target.value)}
                              placeholder={`Opsi ${oIdx + 1}`}
                              data-testid={`csat-pertanyaan-opsi-${idx}-${oIdx}`}
                              className="h-8 px-2 rounded border border-slate-200 text-xs flex-1 max-w-xs disabled:bg-slate-50" />
                            {canEdit && (p.opsi || []).length > 2 && (
                              <button onClick={() => hapusOpsi(idx, oIdx)} data-testid={`csat-pertanyaan-opsi-hapus-${idx}-${oIdx}`}
                                className="text-slate-400 hover:text-rose-600">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        ))}
                        {canEdit && (
                          <button onClick={() => tambahOpsi(idx)} data-testid={`csat-pertanyaan-opsi-tambah-${idx}`}
                            className="text-xs text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1">
                            <Plus className="w-3 h-3" /> Tambah opsi
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  {canEdit && (
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => pindahBaris(idx, -1)} disabled={idx === 0} data-testid={`csat-pertanyaan-naik-${idx}`}
                        className="h-8 w-8 rounded hover:bg-slate-100 text-slate-500 disabled:opacity-30 grid place-items-center">
                        <ArrowUp className="w-4 h-4" />
                      </button>
                      <button onClick={() => pindahBaris(idx, 1)} disabled={idx === pertanyaan.length - 1} data-testid={`csat-pertanyaan-turun-${idx}`}
                        className="h-8 w-8 rounded hover:bg-slate-100 text-slate-500 disabled:opacity-30 grid place-items-center">
                        <ArrowDown className="w-4 h-4" />
                      </button>
                      <button onClick={() => hapusBaris(idx)} data-testid={`csat-pertanyaan-hapus-${idx}`}
                        className="h-8 w-8 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-600 grid place-items-center">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {pertanyaan.length === 0 && (
            <p className="p-4 text-xs text-slate-400 italic text-center border border-dashed border-slate-200 rounded-lg">
              Belum ada pertanyaan. Tambah minimal 1 pertanyaan bintang.
            </p>
          )}
        </div>

        {canEdit && (
          <button onClick={tambahBaris} data-testid="csat-pertanyaan-tambah"
            className="mt-4 text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-medium">
            <Plus className="w-3.5 h-3.5" /> Tambah Pertanyaan
          </button>
        )}
      </section>

      {/* ---------- Template Pesan WA ---------- */}
      <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 mb-6">
        <div className="flex items-center gap-2 mb-1">
          <MessageCircle className="w-4 h-4 text-emerald-600" />
          <h2 className="font-display text-base font-bold text-slate-900">Template Pesan WhatsApp Undangan</h2>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          Dikirim ke pelanggan saat kasir/manager membuat tautan survei dari transaksi penjualan. Kosongkan untuk
          memakai teks bawaan. Placeholder yang didukung: <code className="text-[11px] bg-slate-100 px-1 py-0.5 rounded">{"{nama}"}</code>{" "}
          <code className="text-[11px] bg-slate-100 px-1 py-0.5 rounded">{"{penjual}"}</code>{" "}
          <code className="text-[11px] bg-slate-100 px-1 py-0.5 rounded">{"{cabang}"}</code>{" "}
          <code className="text-[11px] bg-slate-100 px-1 py-0.5 rounded">{"{link}"}</code>{" "}
          — <b>{"{link}"}</b> wajib ada di template manapun.
        </p>
        <textarea
          value={templatePesan} disabled={!canEdit}
          onChange={(e) => setTemplatePesan(e.target.value)}
          rows={4}
          placeholder={templateDefault}
          data-testid="csat-pengaturan-template"
          className="w-full rounded-xl border border-slate-200 p-3 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none disabled:bg-slate-50"
        />
        {!templatePesan.trim() && (
          <p className="text-[11px] text-slate-400 mt-2">
            Kosong = memakai teks bawaan: <span className="italic">"{templateDefault}"</span>
          </p>
        )}
      </section>
    </div>
  );
}
